import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useTheme } from "@/context/ThemeContext";
import { roleLabel } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, KeyRound, Loader2, ExternalLink } from "lucide-react";

function RoleBadge({ label }) {
  return (
    <span className="rounded-full border border-border bg-secondary px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
      {label}
    </span>
  );
}

export default function Profile() {
  const { user, updateProfileLocal } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, setTheme } = useTheme();
  const [firstName, setFirstName] = useState(user.first_name);
  const [lastName, setLastName] = useState(user.last_name);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch(`/auth/profile`, {
        first_name: firstName, last_name: lastName, language: lang, theme,
      });
      updateProfileLocal(data);
      toast.success(t("common.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl p-6 md:p-10" data-testid="profile-page">
      <div className="mb-8">
        <p className="data-label mb-2">{t("profile.breadcrumb")}</p>
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("profile.title")}</h1>
      </div>

      <Card className="p-6">
        <div className="space-y-5">
          <div>
            <Label className="data-label mb-1 block">{t("profile.email")}</Label>
            <Input value={user.email} disabled className="font-mono text-xs" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="data-label mb-1 block">{t("profile.firstName")}</Label>
              <Input
                data-testid="profile-first-name-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={saving}
              />
            </div>
            <div>
              <Label className="data-label mb-1 block">{t("profile.lastName")}</Label>
              <Input
                data-testid="profile-last-name-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
          <div>
            <Label className="data-label mb-1 block">{t("profile.language")}</Label>
            <div className="flex gap-2">
              {["en", "de"].map((l) => (
                <button
                  key={l}
                  data-testid={`profile-lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`rounded-md border px-3 py-1.5 text-sm ${
                    lang === l ? "border-primary bg-primary text-primary-foreground" : "border-border"
                  }`}
                >
                  {l === "en" ? "English" : "Deutsch"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="data-label mb-1 block">{t("profile.theme")}</Label>
            <div className="flex gap-2">
              {["light", "dark", "system"].map((th) => (
                <button
                  key={th}
                  data-testid={`profile-theme-${th}`}
                  onClick={() => setTheme(th)}
                  className={`rounded-md border px-3 py-1.5 text-sm capitalize ${
                    theme === th ? "border-primary bg-primary text-primary-foreground" : "border-border"
                  }`}
                >
                  {t(`profile.theme${th.charAt(0).toUpperCase() + th.slice(1)}`)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="data-label mb-1">{t("profile.roles")}</p>
            {user.dealer_access && user.dealer_access.length > 0 ? (
              <div className="space-y-2.5">
                {user.dealer_access.map((grant, i) => (
                  <div key={grant.dealer_id}>
                    <p className="mb-1 text-xs font-medium text-muted-foreground">
                      {user.dealer_names?.[i] || grant.dealer_id}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {grant.roles.map((r) => (
                        <RoleBadge key={r} label={roleLabel(r, lang)} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {user.roles.map((r) => (
                  <RoleBadge key={r} label={roleLabel(r, lang)} />
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button data-testid="profile-save-btn" onClick={save} disabled={saving} className="gap-1.5">
              <Save className="h-3.5 w-3.5" />
              {t("common.save")}
            </Button>
          </div>
        </div>
      </Card>

      {/* SSO accounts keep their password in Keycloak; local-only accounts (and
          environments without SSO) still change it here. */}
      {user.local_only ? <ChangePasswordCard /> : <PasswordRouter />}
    </div>
  );
}

function PasswordRouter() {
  const [sso, setSso] = useState(undefined); // undefined = loading
  useEffect(() => {
    client
      .get("/auth/sso/status")
      .then((r) => setSso(r.data))
      .catch(() => setSso({ enabled: false }));
  }, []);
  if (sso === undefined) return null;
  return sso.enabled ? <KeycloakPasswordCard sso={sso} /> : <ChangePasswordCard />;
}

function KeycloakPasswordCard({ sso }) {
  const { t } = useI18n();
  return (
    <Card className="mt-6 p-6" data-testid="keycloak-password-card">
      <div className="mb-4">
        <p className="data-label">{t("profile.security")}</p>
        <h2 className="mt-1 text-lg font-semibold">{t("profile.changePassword")}</h2>
        <p className="mt-1 text-xs text-muted-foreground">{t("profile.keycloakPasswordHint")}</p>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        {sso.change_password_url && (
          <Button asChild className="gap-1.5" data-testid="kc-change-password">
            <a href={sso.change_password_url}>
              <KeyRound className="h-3.5 w-3.5" />
              {t("profile.changePassword")}
              <ExternalLink className="h-3 w-3 opacity-70" />
            </a>
          </Button>
        )}
        {sso.reset_password_url && (
          <a
            href={sso.reset_password_url}
            className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
            data-testid="kc-reset-password"
          >
            {t("profile.keycloakForgotPassword")}
          </a>
        )}
      </div>
    </Card>
  );
}

function ChangePasswordCard() {
  const { t } = useI18n();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const valid =
    current.length >= 1 &&
    next.length >= 8 &&
    /[A-Za-z]/.test(next) &&
    /[0-9]/.test(next) &&
    next === confirm;

  const submit = async () => {
    setSaving(true);
    try {
      await client.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success(t("profile.passwordUpdated"));
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("profile.changeFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mt-6 p-6" data-testid="change-password-card">
      <div className="mb-4">
        <p className="data-label">{t("profile.security")}</p>
        <h2 className="mt-1 text-lg font-semibold">{t("profile.changePassword")}</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("profile.changePasswordHint")}
        </p>
      </div>
      <div className="space-y-4">
        <div>
          <Label className="data-label mb-1 block">{t("profile.currentPassword")}</Label>
          <Input
            data-testid="cp-current"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            disabled={saving}
          />
        </div>
        <div>
          <Label className="data-label mb-1 block">{t("profile.newPassword")}</Label>
          <Input
            data-testid="cp-new"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            disabled={saving}
          />
        </div>
        <div>
          <Label className="data-label mb-1 block">{t("profile.confirmNewPassword")}</Label>
          <Input
            data-testid="cp-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={saving}
          />
          {confirm.length > 0 && confirm !== next && (
            <p className="mt-1 text-xs text-destructive">{t("resetPassword.mismatch")}</p>
          )}
        </div>
        <div className="flex justify-end">
          <Button data-testid="cp-submit" onClick={submit} disabled={!valid || saving} className="gap-1.5">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
            {t("profile.changePassword")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
